import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, Loader2, KeyRound, Lightbulb, LightbulbOff, Snowflake, Power, MessageCircle,
  Thermometer, ArrowUp, ArrowDown, Tv, Wind, Home as HomeIcon, CheckCircle2, XCircle,
  ChevronsUp, ChevronsDown, Square, Minus, Plus,
  ChevronUp, ChevronDown, ChevronRight, Undo2, House, Volume2, VolumeX, Moon,
  Frame, Wine,
} from "lucide-react";
import axios from "axios";
import TvRemote from "../components/TvRemote";
import { fetchRoomPublic, unlockRoom, triggerRoomAction, fetchMenus } from "../lib/api";
import { quickIcon, fillQuickText } from "../lib/quickRequests";
import { tr } from "../lib/i18n";
import { R, useLang } from "../lib/roomI18n";
import { LanguageSelector } from "../components/LanguageSelector";
import { getRoomSession, saveRoomSession, clearRoomSession } from "../lib/roomSession";
import { RoomWeather, RoomWeatherTip } from "../components/RoomWeather";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import AppBottomBar from "../components/AppBottomBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const roomApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const ICON_MAP = {
  "lightbulb": Lightbulb,
  "lightbulb-off": LightbulbOff,
  "snowflake": Snowflake,
  "power": Power,
  "power-off": Power,
  "thermometer": Thermometer,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  "tv": Tv,
  "tv-off": Tv,
  "wind": Wind,
};

const CATEGORY_META = {
  light: { label: "Luci", color: "bg-amber-100 border-amber-300 text-amber-900" },
  ac: { label: "Clima", color: "bg-sky-100 border-sky-300 text-sky-900" },
  blind: { label: "Tapparelle", color: "bg-stone-100 border-stone-300 text-stone-900" },
  tv: { label: "TV", color: "bg-indigo-100 border-indigo-300 text-indigo-900" },
  scene: { label: "Scenari", color: "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-900" },
  other: { label: "Altro", color: "bg-neutral-100 border-neutral-300 text-neutral-900" },
};

// Fullboard writes some guests as "COGNOME NOME" (all caps) and others as
// "Nome Cognome": pick the right token and normalise the case.
const firstNameOf = (full) => {
  const t = (full || "").trim().split(/\s+/).filter(Boolean);
  if (!t.length) return "";
  const pick = t.length > 1 && full === full.toUpperCase() ? t[t.length - 1] : t[0];
  return pick.charAt(0).toUpperCase() + pick.slice(1).toLowerCase();
};


// Header hero video per stanza (mp4 + poster in /public/media).
const HERO_VIDEOS = {
  argo: "hero-rooms", bestla: "hero-rooms", clizio: "hero-rooms", damaseno: "hero-rooms",
  efialte: "hero-rooms-dinner", fenja: "hero-rooms-dinner", idas: "hero-rooms-dinner",
};

const ROOM_LOGOS = ["argo", "bestla", "clizio", "damaseno", "efialte", "fenja", "idas"];

export default function RoomPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const heroVideo = HERO_VIDEOS[(slug || "").toLowerCase()];
  const hasHeroVideo = !!heroVideo;
  const [lang, setLang] = useLang();
  const t = (k) => tr(R[k], lang);
  const [frigobarId, setFrigobarId] = useState(null);
  useEffect(() => {
    fetchMenus().then((list) => {
      const arr = Array.isArray(list) ? list : list?.menus || [];
      const m = arr.find((x) => /frigo/i.test(x?.name?.it || x?.name || ""));
      if (m) setFrigobarId(m.id);
    }).catch(() => {});
  }, []);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [guestName, setGuestName] = useState(null);
  const [receptionWa, setReceptionWa] = useState(null);
  const [quickCfg, setQuickCfg] = useState(null);
  // WhatsApp messages stay in Italian (reception reads them); only labels are localized.
  const waIntro = () => fillQuickText(quickCfg?.intro || "Buongiorno, sono {nome} della camera {camera}.", {
    nome: guestName ? firstNameOf(guestName) : "un ospite",
    camera: room?.name || slug,
  });
  const openWa = (msg) => window.open(`https://wa.me/${receptionWa}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  const askReception = () => openWa(`${waIntro()} ${quickCfg?.ask_text ?? "Avrei bisogno di: "}`);
  const QUICK_REQUESTS = quickCfg?.items || [];
  const loc = (it, i18n) => (lang !== "it" && i18n?.[lang]) || it;
  const [unlocking, setUnlocking] = useState(false);
  const [firing, setFiring] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  // Live domotica payload once the room is unlocked.
  const [live, setLive] = useState({ covers: [], lights: [], acs: [], tvs: [] });
  const [liveBusy, setLiveBusy] = useState(false);
  const [pendingKey, setPendingKey] = useState(null); // which control is being fired
  const [frameTv, setFrameTv] = useState([]); // [{key,label,kind,linked}]

  const loadLive = useCallback(async () => {
    if (!unlocked || !pin) return;
    try {
      const { data } = await roomApi.post(`/rooms/${slug}/domotica-live`, { pin });
      setLive(data);
    } catch (_) { /* silent */ }
  }, [unlocked, pin, slug]);

  useEffect(() => {
    if (!unlocked) return;
    loadLive();
    const t = setInterval(loadLive, 10000);
    return () => clearInterval(t);
  }, [unlocked, loadLive]);

  // Fetch Frame TV presence for this room (public, PIN-less presence check).
  useEffect(() => {
    if (!unlocked) return;
    roomApi.get(`/rooms/${slug}/tvs`).then((r) => setFrameTv(r.data.items || [])).catch(() => setFrameTv([]));
  }, [unlocked, slug]);

  const guardedCall = async (key, fn) => {
    setPendingKey(key); setLiveBusy(true);
    try { await fn(); await loadLive(); }
    catch (e) { toast({ title: t("error"), description: e?.response?.data?.detail || e.message }); }
    finally { setPendingKey(null); setLiveBusy(false); }
  };

  const coverAction = (device_id, action) =>
    guardedCall(`cov-${device_id}-${action}`,
      () => roomApi.post(`/rooms/${slug}/cover/${device_id}/control`, { pin, action }));

  const lightToggle = (iot_id, channel, on) =>
    guardedCall(`lt-${iot_id}-${channel}`,
      () => roomApi.post(`/rooms/${slug}/light/${iot_id}/${channel}`, { pin, on }));

  const lightGroup = (group, on) =>
    guardedCall(`gr-${group}-${on}`,
      () => roomApi.post(`/rooms/${slug}/light-group`, { pin, group, on }));

  const acPower = (puid, on) =>
    guardedCall(`ac-${puid}-p`, () => roomApi.post(`/rooms/${slug}/ac/${puid}/power`, { pin, on }));
  const acTemp = (puid, temp) =>
    guardedCall(`ac-${puid}-t`, () => roomApi.post(`/rooms/${slug}/ac/${puid}/temp`, { pin, temp }));

  const tvPower = (device_id, on) =>
    guardedCall(`tv-${device_id}-p`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/power`, { pin, on }));
  const tvHardOff = (device_id) =>
    guardedCall(`tv-${device_id}-off`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/power/hard-off`, { pin }));

  // New Frame TV (multi-account SmartThings) — 1 helper, 3 modes.
  const goodnight = () => {
    if (!window.confirm(t("goodnightConfirm"))) return;
    return guardedCall(`goodnight`, () => roomApi.post(`/rooms/${slug}/scenes/goodnight`, { pin }))
      .then(() => setTimeout(loadLive, 1500));
  };
  const tvVolume = (device_id, delta) =>
    guardedCall(`tv-${device_id}-v${delta}`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/volume`, { pin, delta }));
  const tvMute = (device_id) =>
    guardedCall(`tv-${device_id}-m`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/mute`, { pin }));
  const tvAmbient = (device_id, on) =>
    guardedCall(`tv-${device_id}-a`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/ambient`, { pin, on }));
  const tvArt = (device_id, on) =>
    guardedCall(`tv-${device_id}-art`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/art`, { pin, on }));
  const tvLaunch = (device_id, app) =>
    guardedCall(`tv-${device_id}-app-${app}`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/launch`, { pin, app }));
  const tvChannel = (device_id, direction) =>
    guardedCall(`tv-${device_id}-ch${direction}`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/channel`, { pin, direction }));
  const tvRemote = (device_id, key) =>
    guardedCall(`tv-${device_id}-r${key}`, () => roomApi.post(`/rooms/${slug}/tv/${device_id}/remote`, { pin, key }));

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchRoomPublic(slug);
        setRoom(r);
        // Auto-unlock when this phone already unlocked this room during the stay.
        const s = getRoomSession();
        if (s && s.slug === (r.slug || slug || "").toLowerCase()) {
          try {
            const res = await unlockRoom(slug, s.pin);
            setRoom(res.room); setGuestName(res.guest_name || null); setReceptionWa(res.reception_wa || null); setQuickCfg(res.quick_requests || null); setPin(s.pin); setUnlocked(true);
            saveRoomSession({ slug: s.slug, pin: s.pin, name: res.room?.name, guest: res.guest_name ? firstNameOf(res.guest_name) : "" });
          } catch (err) {
            if (err?.response?.status === 401) clearRoomSession();
          }
        }
      } catch (e) {
        setError(e?.response?.status === 404 ? "notFound" : "connError");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const lockAgain = () => { clearRoomSession(); setUnlocked(false); setPin(""); toast({ title: t("lockedTitle"), description: t("lockedDesc") }); };

  const submitPin = async (e) => {
    e?.preventDefault();
    if (!pin.trim()) return;
    setUnlocking(true);
    try {
      const res = await unlockRoom(slug, pin.trim());
      setRoom(res.room);
      setGuestName(res.guest_name || null);
      setReceptionWa(res.reception_wa || null);
      setQuickCfg(res.quick_requests || null);
      setUnlocked(true);
      saveRoomSession({ slug: (slug || "").toLowerCase(), pin: pin.trim(), name: res.room?.name, guest: res.guest_name ? firstNameOf(res.guest_name) : "" });
    } catch (err) {
      const msg = err?.response?.status === 401 ? t("wrongPin") : t("genericError");
      toast({ title: msg });
      setPin("");
    } finally {
      setUnlocking(false);
    }
  };

  const fireAction = async (action) => {
    setFiring(action.id);
    setLastResult(null);
    try {
      await triggerRoomAction(slug, action.id, pin.trim());
      setLastResult({ ok: true, label: action.label });
      setTimeout(() => setLastResult((r) => (r && r.label === action.label ? null : r)), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("cmdFailed");
      setLastResult({ ok: false, label: action.label, msg });
      toast({ title: action.label, description: msg });
    } finally {
      setFiring(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf3ee] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#faf3ee] flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif text-2xl text-[#2b1410] mb-2">{t(error)}</h1>
          <button onClick={() => navigate("/")} className="text-sm text-[#4a1c17] underline">{t("backHome")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf3ee]">
      {/* Header */}
      <div className={`bg-[#4a1c17] text-[#f7ebdf] relative overflow-hidden ${hasHeroVideo ? "px-4 pt-4 pb-12 sm:p-8 sm:pb-16 min-h-[150px] sm:min-h-[180px] flex flex-col justify-end" : "p-6 sm:p-8 pb-16"}`} data-testid="room-header">
        {hasHeroVideo && (
          <>
            <video className="absolute inset-0 w-full h-full object-cover" src={`/media/${heroVideo}.mp4`} poster={`/media/${heroVideo}-poster.jpg`} autoPlay loop muted playsInline preload="metadata" aria-hidden="true" data-testid="room-hero-video" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/95 via-[#2b1410]/45 to-[#2b1410]/20 pointer-events-none" />
          </>
        )}
        <div className="absolute top-3 right-3 z-20" data-testid="room-lang-selector">
          <LanguageSelector lang={lang} setLang={setLang} onDark />
        </div>
        <div className="relative max-w-2xl mx-auto w-full flex flex-wrap items-center justify-between gap-3">
          {ROOM_LOGOS.includes((slug || "").toLowerCase()) ? (
            <div className="flex items-center min-w-0 pt-6 sm:pt-0">
              <img
                src={`/media/room-logo-${slug.toLowerCase()}.png`}
                alt={room.name}
                className="h-28 w-28 sm:h-36 sm:w-36 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
                data-testid="room-logo"
              />
              <h1 className="sr-only" data-testid="room-name">{room.name}</h1>
            </div>
          ) : (
          <div className="flex items-center gap-3 min-w-0">
            <HomeIcon className="h-7 w-7 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest opacity-70">{t("room")}</div>
              <h1 className="font-serif text-3xl sm:text-4xl font-bold drop-shadow-lg" data-testid="room-name">{room.name}</h1>
            </div>
          </div>
          )}
          <div className="w-full sm:w-auto grid grid-cols-2 sm:flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate("/")}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#4a1c17] px-4 py-2.5 text-sm font-bold shadow-md hover:bg-[#f7ebdf] active:scale-95 transition-[background-color,transform]"
              data-testid="room-back-btn"
              aria-label={t("home")}
            >
              <HomeIcon className="h-5 w-5" /> {t("home")}
            </button>
            {frigobarId && (
              <button
                onClick={() => navigate(`/menu/${frigobarId}`)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full bg-[#C48B31] text-white px-4 py-2.5 text-sm font-bold shadow-md hover:bg-[#a9761f] active:scale-95 transition-[background-color,transform]"
                data-testid="room-frigobar-btn"
              >
                <Wine className="h-5 w-5" /> <span className="whitespace-nowrap">{t("frigobar")}</span>
              </button>
            )}
          </div>
        </div>
        <svg className="absolute bottom-[-1px] left-0 right-0 w-full" viewBox="0 0 1200 40" preserveAspectRatio="none" aria-hidden>
          <path d="M0,40 C300,10 900,10 1200,40 L1200,40 L0,40 Z" fill="#faf3ee" />
        </svg>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6" data-testid="room-page">
        {!unlocked ? (
          <form onSubmit={submitPin} className="rounded-2xl bg-white border border-[#e8dcc8] p-6 sm:p-8 shadow-sm text-center" data-testid="room-pin-form">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#4a1c17]/10 text-[#4a1c17] flex items-center justify-center">
              <KeyRound className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-serif text-xl font-bold text-[#2b1410]">{t("pinTitle")}</h2>
            <p className="mt-2 text-sm text-[#7c5a4e]">{t("pinHint")}</p>
            <p className="mt-1 text-xs text-[#a08477]">{t("pinRemember")}</p>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
              className="mt-5 mx-auto block w-40 text-center text-2xl tracking-[0.4em] font-mono rounded-xl border-2 border-[#e8dcc8] focus:border-[#4a1c17] focus:ring-2 focus:ring-[#4a1c17]/20 outline-none px-3 py-3 bg-[#faf3ee]"
              placeholder="••••"
              data-testid="room-pin-input"
            />
            <button
              type="submit"
              disabled={unlocking || !pin.trim()}
              data-testid="room-pin-submit"
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] disabled:opacity-60 text-[#f7ebdf] px-6 py-2.5 text-sm font-medium"
            >
              {unlocking ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("verifying")}</> : t("unlock")}
            </button>
          </form>
        ) : (
          <div className="space-y-6" data-testid="room-panel">
            <div className="flex items-end justify-between gap-3 -mb-3">
              {guestName ? (
                <div data-testid="room-guest-greeting">
                  <div className="font-serif text-2xl font-bold text-[#2b1410]">{t("hello")} {firstNameOf(guestName)}</div>
                  <div className="text-xs text-[#7c5a4e]">{t("welcomeSub")}</div>
                  <RoomWeather lang={lang} />
                </div>
              ) : <div><RoomWeather lang={lang} /></div>}
              <button type="button" onClick={lockAgain} className="inline-flex items-center gap-1 text-xs text-[#7c5a4e] hover:text-[#4a1c17] underline-offset-2 hover:underline shrink-0" data-testid="room-lock-btn">
                <KeyRound className="h-3 w-3" /> {t("lockForget")}
              </button>
            </div>
            {receptionWa && (
              <div className="space-y-2" data-testid="room-ask-block">
                <button
                  type="button"
                  onClick={askReception}
                  data-testid="room-ask-btn"
                  className="w-full flex items-center gap-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 shadow-md active:scale-[0.99] transition-[background-color,transform]"
                >
                  <span className="h-10 w-10 shrink-0 rounded-full bg-white/15 flex items-center justify-center"><MessageCircle className="h-5 w-5" /></span>
                  <span className="text-left min-w-0">
                    <span className="block font-bold leading-tight">{loc(quickCfg?.ask_title || t("askTitle"), quickCfg?.ask_title_i18n)}</span>
                    {(quickCfg?.ask_subtitle ?? "…") && (
                      <span className="block text-xs opacity-90">{loc(quickCfg?.ask_subtitle ?? "", quickCfg?.ask_subtitle_i18n)}</span>
                    )}
                  </span>
                </button>
                {QUICK_REQUESTS.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="room-quick-requests">
                  {QUICK_REQUESTS.map(({ id, label, label_i18n, icon, text }) => {
                    const Icon = quickIcon(icon);
                    return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => openWa(`${waIntro()} ${fillQuickText(text, { nome: guestName ? firstNameOf(guestName) : "un ospite", camera: room?.name || slug })}`)}
                      data-testid={`room-quick-${id}`}
                      className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 px-2 py-3 text-xs font-semibold leading-tight text-center active:scale-[0.97] transition-[background-color,transform]"
                    >
                      <Icon className="h-5 w-5 text-emerald-700" />
                      <span>{loc(label, label_i18n)}</span>
                    </button>
                    );
                  })}
                </div>
                )}
              </div>
            )}
            <RoomWeatherTip lang={lang} />
            {lastResult && (
              <div className={`rounded-lg border px-4 py-3 flex items-center gap-2 text-sm ${lastResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="room-last-result">
                {lastResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                <span><strong>{lastResult.label}</strong> — {lastResult.ok ? t("sentAlexa") : (lastResult.msg || t("error").toLowerCase())}</span>
              </div>
            )}

            {/* Modalità Notte — one-tap scene */}
            <button
              onClick={goodnight}
              disabled={liveBusy}
              data-testid="scene-goodnight"
              className="w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-br from-[#2b1410] via-[#4a1c17] to-[#2b1410] hover:from-[#3a1b18] hover:via-[#5a241d] hover:to-[#3a1b18] text-[#f7ebdf] py-4 font-semibold shadow-lg shadow-[#4a1c17]/25 active:scale-[0.98] disabled:opacity-50 transition-all border border-[#f7ebdf]/10"
            >
              <Moon className="h-5 w-5 text-[#f7ebdf]" />
              <span className="text-base tracking-wide">{t("goodnight")}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#f7ebdf]/70 ml-1">{t("goodnightSub")}</span>
            </button>

            {/* Live Tapparelle */}
            {live.covers.length > 0 && (
              <section data-testid="live-covers">
                <h3 className="font-serif text-sm uppercase tracking-widest text-[#7c5a4e] mb-2 px-1">{t("blinds")}</h3>
                {live.covers.map((c) => {
                  const pct = c.percent_state != null ? 100 - c.percent_state : null;
                  return (
                    <div key={c.device_id} className="rounded-xl border border-stone-300 bg-stone-50 p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-stone-900">{c.name}</span>
                        {pct != null && <span className="text-xs text-stone-600">{t("openPct")} {pct}%</span>}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <button data-testid={`cover-${c.device_id}-open`} disabled={liveBusy} onClick={() => coverAction(c.device_id, "open")} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1"><ChevronsUp className="h-4 w-4" /> {t("open")}</button>
                        <button data-testid={`cover-${c.device_id}-stop`} disabled={liveBusy} onClick={() => coverAction(c.device_id, "stop")} className="rounded-lg bg-amber-500 hover:bg-amber-600 text-white py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1"><Square className="h-4 w-4" /> {t("stop")}</button>
                        <button data-testid={`cover-${c.device_id}-close`} disabled={liveBusy} onClick={() => coverAction(c.device_id, "close")} className="rounded-lg bg-[#4a1c17] hover:bg-[#2b1410] text-white py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1"><ChevronsDown className="h-4 w-4" /> {t("close")}</button>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {/* Live Luci — group headers + individual toggles */}
            {live.lights.length > 0 && (
              <section data-testid="live-lights">
                <h3 className="font-serif text-sm uppercase tracking-widest text-[#7c5a4e] mb-2 px-1">{t("lights")}</h3>
                {["bagno", "camera", ""].map((grp) => {
                  const items = live.lights.filter((l) => (l.group || "") === grp);
                  if (items.length === 0) return null;
                  const grpLabel = grp === "bagno" ? t("bathroom") : grp === "camera" ? t("bedroom") : t("others");
                  return (
                    <div key={grp || "other"} className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-amber-900">{grpLabel}</span>
                        {grp && (
                          <div className="flex gap-1">
                            <button data-testid={`lightgroup-${grp}-on`} disabled={liveBusy} onClick={() => lightGroup(grp, true)} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 disabled:opacity-60">{t("allOn")}</button>
                            <button data-testid={`lightgroup-${grp}-off`} disabled={liveBusy} onClick={() => lightGroup(grp, false)} className="rounded-full bg-stone-500 hover:bg-stone-600 text-white text-xs px-3 py-1 disabled:opacity-60">{t("allOff")}</button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {items.map((l) => {
                          const isOn = l.state === "on";
                          return (
                            <button
                              key={`${l.iot_id}-${l.channel}`}
                              data-testid={`light-${l.iot_id}-${l.channel}`}
                              disabled={liveBusy || l.offline}
                              onClick={() => lightToggle(l.iot_id, l.channel, !isOn)}
                              className="group flex flex-col items-center gap-2 px-1 py-2 text-center min-w-0 disabled:opacity-50"
                            >
                              <span className={`h-16 w-16 rounded-full border-[3px] border-[#4a1c17] flex items-center justify-center shrink-0 transition-[background-color,box-shadow,transform] duration-150 group-active:scale-95 ${isOn ? "bg-[#C48B31] text-[#2b1410] shadow-[0_0_0_4px_rgba(196,139,49,0.3),0_4px_10px_rgba(0,0,0,0.25)]" : "bg-[#f7ebdf] text-[#4a1c17] shadow-[0_3px_8px_rgba(0,0,0,0.2)]"}`}>
                                {isOn ? <Lightbulb className="h-7 w-7" /> : <LightbulbOff className="h-7 w-7" />}
                              </span>
                              <span className={`text-xs font-semibold leading-tight break-words w-full ${isOn ? "text-[#2b1410]" : "text-stone-700"}`}>{l.name}{l.offline && <span className="block text-[10px] font-normal text-stone-500">{t("unavailable")}</span>}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {/* Live AC */}
            {live.acs.length > 0 && (
              <section data-testid="live-acs">
                <h3 className="font-serif text-sm uppercase tracking-widest text-[#7c5a4e] mb-2 px-1">{t("ac")}</h3>
                {live.acs.map((ac) => {
                  const isOn = !!ac.power;
                  return (
                    <div key={ac.puid} className="rounded-xl border border-sky-300 bg-sky-50 p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold text-sm text-sky-950">{ac.name}</div>
                          {!ac.offline && <div className="text-[11px] text-sky-800">{t("ambient")} {ac.ambient_temp}°C · {t("target")} {ac.target_temp}°C</div>}
                        </div>
                        <button data-testid={`ac-${ac.puid}-power`} disabled={liveBusy || ac.offline} onClick={() => acPower(ac.puid, !isOn)} className={`inline-flex items-center gap-1 rounded-full px-3 py-2 font-semibold text-white disabled:opacity-60 ${isOn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-stone-500 hover:bg-stone-600"}`}><Power className="h-4 w-4" /> {isOn ? "ON" : "OFF"}</button>
                      </div>
                      {isOn && !ac.offline && (
                        <div className="flex items-center justify-center gap-3 mt-2">
                          <button data-testid={`ac-${ac.puid}-minus`} disabled={liveBusy || ac.target_temp <= 16} onClick={() => acTemp(ac.puid, ac.target_temp - 1)} className="h-11 w-11 rounded-full bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40"><Minus className="h-5 w-5 mx-auto" /></button>
                          <div className="text-3xl font-serif font-bold text-sky-950 min-w-[60px] text-center">{ac.target_temp}°</div>
                          <button data-testid={`ac-${ac.puid}-plus`} disabled={liveBusy || ac.target_temp >= 30} onClick={() => acTemp(ac.puid, ac.target_temp + 1)} className="h-11 w-11 rounded-full bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40"><Plus className="h-5 w-5 mx-auto" /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            )}

            {/* Frame TV — telecomando ospite (OAuth per stanza) */}
            {frameTv.length > 0 && (
              <section data-testid="frame-tv-controls">
                <h3 className="font-serif text-sm uppercase tracking-widest text-[#7c5a4e] mb-2 px-1">TV</h3>
                {frameTv.map((tv) => (
                  <div key={tv.key} className="mb-3">
                    {frameTv.length > 1 && <div className="text-xs font-semibold text-[#4a1c17] mb-1 px-1">{tv.label}</div>}
                    <TvRemote
                      slug={tv.key}
                      kind={tv.kind}
                      lang={lang}
                      getStatus={() => roomApi.post(`/rooms/${slug}/tv/status`, { pin, tv: tv.key }).then((r) => r.data)}
                      sendCommand={(action, value) => roomApi.post(`/rooms/${slug}/tv/command`, { pin, tv: tv.key, action, value }).then((r) => r.data)}
                    />
                  </div>
                ))}
              </section>
            )}

            {/* Legacy TV section (shared-account SmartThings) — hidden. */}
            {false && (live.tvs?.length > 0) && (
              <section data-testid="live-tvs">
                <h3 className="font-serif text-sm uppercase tracking-widest text-[#7c5a4e] mb-2 px-1">TV</h3>
                {live.tvs.map((tv) => {
                  const st = tv.status || {};
                  const isOn = st.switch === "on";
                  const online = st.online !== false;
                  return (
                    <div key={tv.device_id} className="rounded-xl border border-indigo-300 bg-indigo-50 p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold text-sm text-indigo-950 flex items-center gap-1.5">
                            <Tv className="h-4 w-4" /> {tv.friendly_label || tv.label}
                            {tv.is_frame && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-800 text-white">FRAME</span>}
                          </div>
                          <div className="text-[11px] text-indigo-800">
                            {online ? (isOn ? `Accesa · ${st.input || ""}` : (tv.is_frame ? "Modalità Quadro" : "Spenta")) : "Offline"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {tv.is_frame ? (
                            <>
                              <button
                                data-testid={`tv-${tv.device_id}-toggle`}
                                disabled={liveBusy || !online}
                                onClick={() => tvPower(tv.device_id, !isOn)}
                                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-white disabled:opacity-60 transition-all active:scale-95 shadow-md ${isOn ? "bg-gradient-to-br from-[#4a1c17] to-[#2b1410] hover:from-[#5a241d] hover:to-[#3a1b18]" : "bg-gradient-to-br from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900"}`}
                              >
                                <Tv className="h-4 w-4" /> {isOn ? "TV → Quadro" : "Quadro → TV"}
                              </button>
                              {isOn && (
                                <button
                                  data-testid={`tv-${tv.device_id}-hard-off`}
                                  disabled={liveBusy || !online}
                                  onClick={() => tvHardOff(tv.device_id)}
                                  title="Spegni davvero (bypass Quadro)"
                                  className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-stone-300 text-stone-500 hover:text-stone-700 hover:bg-stone-100 active:scale-95 disabled:opacity-40 transition-all"
                                >
                                  <Power className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              data-testid={`tv-${tv.device_id}-power`}
                              disabled={liveBusy || !online}
                              onClick={() => tvPower(tv.device_id, !isOn)}
                              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-white disabled:opacity-60 transition-all active:scale-95 shadow-md ${isOn ? "bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800" : "bg-gradient-to-br from-stone-500 to-stone-700 hover:from-stone-600 hover:to-stone-800"}`}
                            >
                              <Power className="h-4 w-4" /> {isOn ? "ON" : "OFF"}
                            </button>
                          )}
                        </div>
                      </div>
                      {isOn && online && (
                        <>
                          {/* ==================== TELECOMANDO ==================== */}
                          <div className="mx-auto max-w-[280px] rounded-[28px] bg-gradient-to-b from-[#4a1c17] to-[#2b1410] p-4 shadow-[0_12px_28px_-8px_rgba(74,28,23,0.55)] border border-[#5d2a20]">
                            {/* Volume + Mute — riga superiore */}
                            <div className="flex items-center justify-center gap-2 mb-3">
                              <button
                                data-testid={`tv-${tv.device_id}-vol-down`}
                                disabled={liveBusy}
                                onClick={() => tvVolume(tv.device_id, -2)}
                                className="h-11 w-11 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf] disabled:opacity-40 transition-all backdrop-blur-sm flex items-center justify-center border border-[#f7ebdf]/10"
                                title="Volume giù"
                              ><Minus className="h-5 w-5" /></button>
                              <button
                                data-testid={`tv-${tv.device_id}-mute`}
                                disabled={liveBusy}
                                onClick={() => tvMute(tv.device_id)}
                                className="h-11 px-5 rounded-full bg-[#f7ebdf]/15 hover:bg-[#f7ebdf]/25 active:scale-95 text-[#f7ebdf] text-[11px] font-semibold disabled:opacity-40 transition-all flex items-center gap-1.5 border border-[#f7ebdf]/10"
                              ><VolumeX className="h-4 w-4" />Muto</button>
                              <button
                                data-testid={`tv-${tv.device_id}-vol-up`}
                                disabled={liveBusy}
                                onClick={() => tvVolume(tv.device_id, 2)}
                                className="h-11 w-11 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf] disabled:opacity-40 transition-all flex items-center justify-center border border-[#f7ebdf]/10"
                                title="Volume su"
                              ><Plus className="h-5 w-5" /></button>
                            </div>

                            {/* D-Pad circolare */}
                            <div className="relative mx-auto w-40 h-40 mb-3">
                              {/* anello esterno */}
                              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#f7ebdf]/10 to-[#f7ebdf]/5 border border-[#f7ebdf]/15 shadow-inner" />
                              {/* UP */}
                              <button
                                data-testid={`tv-${tv.device_id}-r-up`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "up")}
                                className="absolute top-1 left-1/2 -translate-x-1/2 h-9 w-14 rounded-full text-[#f7ebdf]/85 hover:text-[#f7ebdf] hover:bg-[#f7ebdf]/10 active:scale-90 disabled:opacity-40 transition-all flex items-center justify-center"
                              ><ChevronUp className="h-5 w-5" /></button>
                              {/* DOWN */}
                              <button
                                data-testid={`tv-${tv.device_id}-r-down`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "down")}
                                className="absolute bottom-1 left-1/2 -translate-x-1/2 h-9 w-14 rounded-full text-[#f7ebdf]/85 hover:text-[#f7ebdf] hover:bg-[#f7ebdf]/10 active:scale-90 disabled:opacity-40 transition-all flex items-center justify-center"
                              ><ChevronDown className="h-5 w-5" /></button>
                              {/* LEFT */}
                              <button
                                data-testid={`tv-${tv.device_id}-r-left`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "left")}
                                className="absolute left-1 top-1/2 -translate-y-1/2 h-14 w-9 rounded-full text-[#f7ebdf]/85 hover:text-[#f7ebdf] hover:bg-[#f7ebdf]/10 active:scale-90 disabled:opacity-40 transition-all flex items-center justify-center"
                              ><ChevronLeft className="h-5 w-5" /></button>
                              {/* RIGHT */}
                              <button
                                data-testid={`tv-${tv.device_id}-r-right`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "right")}
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-14 w-9 rounded-full text-[#f7ebdf]/85 hover:text-[#f7ebdf] hover:bg-[#f7ebdf]/10 active:scale-90 disabled:opacity-40 transition-all flex items-center justify-center"
                              ><ChevronRight className="h-5 w-5" /></button>
                              {/* OK central */}
                              <button
                                data-testid={`tv-${tv.device_id}-r-ok`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "ok")}
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-gradient-to-br from-[#f7ebdf] to-[#e5d7bd] hover:from-white hover:to-[#efe0c5] active:scale-95 text-[#4a1c17] text-xs font-black shadow-[0_4px_12px_rgba(0,0,0,0.35)] disabled:opacity-40 transition-all border border-[#4a1c17]/20"
                              >OK</button>
                            </div>

                            {/* Back / Home / Canali */}
                            <div className="grid grid-cols-4 gap-2 mb-3">
                              <button
                                data-testid={`tv-${tv.device_id}-r-back`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "back")}
                                className="h-9 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf]/90 text-[10px] disabled:opacity-40 transition-all flex items-center justify-center gap-1 border border-[#f7ebdf]/10"
                              ><Undo2 className="h-3 w-3" />Back</button>
                              <button
                                data-testid={`tv-${tv.device_id}-ch-down`}
                                disabled={liveBusy}
                                onClick={() => tvChannel(tv.device_id, "down")}
                                className="h-9 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf]/90 text-[10px] disabled:opacity-40 transition-all flex items-center justify-center border border-[#f7ebdf]/10"
                              >‹ CH</button>
                              <button
                                data-testid={`tv-${tv.device_id}-ch-up`}
                                disabled={liveBusy}
                                onClick={() => tvChannel(tv.device_id, "up")}
                                className="h-9 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf]/90 text-[10px] disabled:opacity-40 transition-all flex items-center justify-center border border-[#f7ebdf]/10"
                              >CH ›</button>
                              <button
                                data-testid={`tv-${tv.device_id}-r-home`}
                                disabled={liveBusy}
                                onClick={() => tvRemote(tv.device_id, "home")}
                                className="h-9 rounded-full bg-[#f7ebdf]/10 hover:bg-[#f7ebdf]/20 active:scale-95 text-[#f7ebdf]/90 text-[10px] disabled:opacity-40 transition-all flex items-center justify-center gap-1 border border-[#f7ebdf]/10"
                              ><House className="h-3 w-3" />Home</button>
                            </div>

                            {/* App shortcuts */}
                            <div className="grid grid-cols-4 gap-1.5">
                              {["netflix","amazon","youtube","rai"].map((app) => (
                                <button
                                  key={app}
                                  data-testid={`tv-${tv.device_id}-app-${app}`}
                                  disabled={liveBusy}
                                  onClick={() => tvLaunch(tv.device_id, app)}
                                  className="h-8 rounded-lg bg-[#f7ebdf]/12 hover:bg-[#f7ebdf]/22 active:scale-95 text-[#f7ebdf] text-[9px] font-semibold disabled:opacity-40 capitalize transition-all border border-[#f7ebdf]/10 tracking-wide"
                                >{app === "amazon" ? "Prime" : app === "rai" ? "RaiPlay" : app}</button>
                              ))}
                            </div>
                          </div>
                          {/* ==================== FINE TELECOMANDO ==================== */}

                          {tv.is_frame && (
                            <div className="text-center text-[10px] text-[#7a5a48] mt-2 italic">
                              Tocca <span className="font-semibold text-[#4a1c17]">Quadro</span> qui sopra per attivare la Modalità Arte (nessun vero spegnimento)
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </section>
            )}

            {/* Legacy Alexa/Voice Monkey actions removed — all controls now live via SmartThings/Tuya/Livolo/Hisense */}
            {live.covers.length === 0 && live.lights.length === 0 && live.acs.length === 0 && (live.tvs?.length || 0) === 0 && frameTv.length === 0 && (
              <div className="rounded-lg bg-white border border-[#e8dcc8] p-6 text-center text-sm text-[#7c5a4e]">
                {t("noControls")}
              </div>
            )}
            <p className="text-[11px] text-[#7c5a4e] text-center opacity-75 pt-3">
              {t("realtime")}
            </p>
          </div>
        )}
      </main>
      <AppBottomBar current="none" lang={lang} />
      <Toaster />
    </div>
  );
}
